import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { AsyncStage } from '../reduxCommon';
import {
	UserGroup,
	UserGroups,
	UserGroupWithMembers,
} from '../models/userGroup';
import findIndex from 'lodash/findIndex';

interface UserGroupState {
	stage: AsyncStage;
	error: string;
	userGroup: UserGroup;
	userGroups: UserGroups;
	userGroupWithMembers: UserGroupWithMembers;
}

const initialState: UserGroupState = {
	stage: AsyncStage.NoOp,
	error: '',
	userGroup: {
		id: '',
		created: '',
		updated: '',
		name: '',
		description: '',
		users: [],
		externalUsers: [],
		companyId: '',
	},
	userGroups: {
		list: [],
		count: 0,
		filter: {
			searchText: '',
			page: 0,
			take: 50,
			order: 'created',
			direction: 'desc',
			where: {},
		},
		initialized: false,
	},
	userGroupWithMembers: {
		id: '',
		created: '',
		updated: '',
		name: '',
		description: '',
		users: [],
		externalUsers: [],
		companyId: '',
		totalMembers: 0,
	},
};

const userGroupSlice = createSlice({
	name: 'userGroup',
	initialState,
	reducers: {
		asyncCallStart: (state: UserGroupState) => {
			state.stage = AsyncStage.Busy;
		},
		asyncCallDone: (state: UserGroupState) => {
			state.stage = AsyncStage.NoOp;
		},
		asyncCallError: (state: UserGroupState, action: PayloadAction<any>) => {
			state.stage = AsyncStage.Error;
			state.error = action.payload.message || 'UNKNOWN';
		},
		fetchUserGroupSuccess: (
			state: UserGroupState,
			action: PayloadAction<UserGroup>
		) => {
			state.userGroup = action.payload;
			state.stage = AsyncStage.Success;
		},
		fetchUserGroupsSuccess: (
			state: UserGroupState,
			action: PayloadAction<{
				list: UserGroup[];
				count: number;
			}>
		) => {
			state.userGroups = {
				...state.userGroups,
				list: action.payload.list,
				count: action.payload.count,
				initialized: true,
			};
			state.stage = AsyncStage.Success;
		},
		createUserGroupSuccess: (
			state: UserGroupState,
			action: PayloadAction<UserGroup>
		) => {
			state.userGroups = {
				...state.userGroups,
				list: [action.payload, ...state.userGroups.list],
				count: state.userGroups.count + 1,
			};
			state.stage = AsyncStage.Success;
		},
		updateUserGroupSuccess: (
			state: UserGroupState,
			action: PayloadAction<UserGroup>
		) => {
			const list = [...state.userGroups.list];
			const group = action.payload;
			const groupIndex = findIndex(list, (g) => g.id === group.id);
			if (groupIndex >= 0) {
				list[groupIndex] = group;
			}
			state.userGroups = {
				...state.userGroups,
				list,
			};
			if (state.userGroup.id === group.id) {
				state.userGroup = group;
			}
			state.stage = AsyncStage.Success;
		},
		deleteUserGroupSuccess: (
			state: UserGroupState,
			action: PayloadAction<string>
		) => {
			const groupId = action.payload;
			state.userGroups = {
				...state.userGroups,
				list: state.userGroups.list.filter((g) => g.id !== groupId),
				count: state.userGroups.count - 1,
			};
			state.stage = AsyncStage.Success;
		},
		updateUserGroupsFilter: (
			state: UserGroupState,
			action: PayloadAction<any>
		) => {
			state.userGroups = {
				...state.userGroups,
				filter: {
					...state.userGroups.filter,
					...action.payload,
				},
			};
		},
	},
});

export const {
	asyncCallStart,
	asyncCallDone,
	asyncCallError,
	fetchUserGroupSuccess,
	fetchUserGroupsSuccess,
	createUserGroupSuccess,
	updateUserGroupSuccess,
	deleteUserGroupSuccess,
	updateUserGroupsFilter,
} = userGroupSlice.actions;

export default userGroupSlice.reducer;
